import {
  AWAITING_RUN_STATES,
  TERMINAL_RUN_STATES,
  type RunDetail,
  type RunResume,
  type RunState,
  type RunSummary,
} from '@ni/shared';

/**
 * The run that made the subject's dossier: the latest complete research run.
 * Same rule the server pins the stable brief by (subject-record.service.ts),
 * so UI gated on it cannot disagree with what the office reads.
 */
export function completedResearchRun(runs: RunSummary[]): RunSummary | undefined {
  return runs
    .filter((run) => run.kind === 'research' && run.state === 'complete')
    .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))[0];
}

/** The states a run sits in while it waits on a human. */
export function isAwaiting(state: RunState): boolean {
  return (AWAITING_RUN_STATES as readonly string[]).includes(state);
}

export function isTerminal(state: RunState): boolean {
  return (TERMINAL_RUN_STATES as readonly string[]).includes(state);
}

/**
 * The states in which the **pipeline** owns the run and something is in flight —
 * the client's one definition of "working", so the strip's pulsing dot, the 3s
 * poll and the controls a moving run takes away all key off the same set.
 *
 * `checkpointed` belongs here even though no invocation is executing at that
 * instant: the leg stopped deliberately at a phase boundary and its successor is
 * already enqueued, so the run is between legs, not waiting on anyone. Nothing
 * is asked of the office and nothing has gone wrong — which is exactly why it
 * must not read as either an awaiting state or a silence.
 */
export function isWorkingState(state: RunState): boolean {
  return state === 'created' || state === 'running' || state === 'checkpointed';
}

/** The code a wrong-person answer writes onto the run it stopped (§6.1/§6.2). */
import { RUN_ERROR_CODES } from '@ni/shared';

export const IDENTITY_WITHDRAWN = RUN_ERROR_CODES.IDENTITY_WITHDRAWN;

/**
 * A run the wrong-person unwind withdrew (ui-design §4.5). The failure says so
 * outright: both `RunDetail.error` and `RunSummary.error` carry the code, so
 * nothing here is inferred from the absence of an identity.
 *
 * It is inert history: no strip, no Resume (the server refuses one anyway), and
 * the tab shows its pre-run framing instead.
 */
export function isWithdrawnRun(run: {
  state: RunState;
  error: { code: string | null; message: string } | null;
}): boolean {
  return run.state === 'failed' && run.error?.code === IDENTITY_WITHDRAWN;
}

/** The code the Cancel press writes onto the run it stopped (owner ruling, 2026-09-10). */
export const CANCELLED_BY_OFFICE = RUN_ERROR_CODES.CANCELLED_BY_OFFICE;

/**
 * **A run the office stopped.** The withdrawal's shape one door over: a `failed`
 * row whose code says a person ended it, so the surfaces read it off the row
 * rather than inferring it.
 */
export function isCancelledRun(run: {
  state: RunState;
  error: { code: string | null; message: string } | null;
}): boolean {
  return run.state === 'failed' && run.error?.code === CANCELLED_BY_OFFICE;
}

/**
 * **The failures that are not failures** — the office ended this run itself,
 * either by rejecting the identity it was researching or by pressing Cancel.
 *
 * One predicate rather than two conditions at each call site, because the three
 * surfaces that ask it all want the same answer for both: the run is inert
 * history, there is no Resume, and the page goes back to what it offered before
 * the run started. The server agrees from its own side (`isOfficeEndedRun`) —
 * neither code reaches the needs-action inbox, and both are refused a resume.
 *
 * The two still differ in **what the record holds afterwards**, which is why
 * they stay separate codes: a withdrawal took the research down with it, and a
 * cancel left every claim the run had already landed exactly where it was.
 */
export function isOfficeEndedRun(run: {
  state: RunState;
  error: { code: string | null; message: string } | null;
}): boolean {
  return isWithdrawnRun(run) || isCancelledRun(run);
}

/** The code the engine writes when a quick search's candidate merge came back
 *  unreadable twice (2026-09-29). */
export const IDENTITY_MATCH_FAILED = RUN_ERROR_CODES.IDENTITY_MATCH_FAILED;

/**
 * **A failed run with nothing to resume**: one the office ended itself, or a
 * quick search whose candidate match the engine could not read. The page goes
 * back to offering the button that starts a run, and there is no Resume — the
 * server refuses one on all three codes.
 *
 * The match failure is not folded into {@link isOfficeEndedRun}: nobody here
 * chose it, so it still reads "Research failed" with its "Contact Arbiter" line,
 * and it still belongs in the inbox. What it shares is only the way forward — a
 * Resume would ask the same engine the same question, so the way back is a new
 * quick search once Arbiter has fixed it
 * (`docs/notes/2026-09-29-identity-merge-fallback.md`).
 */
export function isRestartOnlyRun(run: {
  state: RunState;
  error: { code: string | null; message: string } | null;
}): boolean {
  return (
    isOfficeEndedRun(run) || (run.state === 'failed' && run.error?.code === IDENTITY_MATCH_FAILED)
  );
}

/**
 * **Resume is offered on a run the server would resume** (`RunResume`), and not
 * while a Resume it accepted is waiting for the agent: then the run's own line
 * says it is resuming, and a second press would only queue a second dispatch.
 */
export function offersResume(run: { resume: RunResume | null }): boolean {
  return run.resume !== null && run.resume.status !== 'queued';
}

/**
 * **Which buttons that start a new run the person's research tab shows.**
 *
 * None while the latest run is working, and none while it is a failed run the
 * server would resume, whether Resume is on offer or already waiting for the
 * agent: the run block is then the only action. A failed run Resume refuses
 * (`resume: null`: a withdrawn identity, a cancel, an unreadable identity match)
 * gives the page back to the button that starts one.
 *
 * `canKickOff` is the quick search, for a person with no identity yet;
 * `canRunDossier` the first research run, once the identity stands.
 */
export function newRunOffers({
  latestRun,
  identity,
  completedResearch,
  demo,
}: {
  latestRun: { state: RunState; resume: RunResume | null } | null;
  identity: boolean;
  completedResearch: boolean;
  demo: boolean;
}): { canKickOff: boolean; canRunDossier: boolean } {
  const working = latestRun !== null && !isTerminal(latestRun.state);
  const free = !demo && !working && latestRun?.resume == null;
  return {
    canKickOff: free && !identity,
    canRunDossier: free && identity && !completedResearch,
  };
}

/**
 * The "Resuming…" window (ui-design §4.3): every gating decision is answered,
 * so the continuation is enqueued, but the state only flips to `running` when
 * the pipeline invocation starts — which lags by dispatch and cold start.
 *
 * **`run.gated` is the window**, and it is the server's own answer to the only
 * question that decides it: does a pending gating decision still stand on this
 * run? It is computed by the same `gating.ts` predicate the continuation's leg
 * name is computed from, so the client cannot hold a second opinion about what
 * a run is waiting on — and because it rides the polled read, the window
 * survives a reload, holds in a second tab, and covers the identity kinds and
 * the adjudication park with one rule instead of two.
 *
 * It replaces the `adjudications` counter this used to key on. That counter said
 * the same thing for exactly one state (`awaiting_adjudication`, all rows
 * answered ⇒ no pending gate) and said nothing at all for the identity kinds,
 * which is why they needed the mark. `gated` subsumes it, so the counter goes
 * back to being what the strip's "N findings need your review" line reads and
 * nothing more.
 *
 * The in-memory mark (`ResumingProvider`) is an **immediate-feedback overlay**
 * for the tab that just answered: answering invalidates the run query, and until
 * that refetch lands the polled row still says `gated`. So the mark does claim
 * resuming while a gate is still open — that is what makes the overlay work, and
 * the order it is read in buys nothing (`!gated || marked` is commutative).
 * Which is exactly why it must never be set on the adjudication answer path:
 * `run-strip.tsx` hides `FindingReview` on resuming, so a mark at a
 * multi-question park would unmount a panel still holding unanswered questions.
 * Only the pick and prose paths set it.
 *
 * A `failed` run someone pressed Resume on sits in the same window for a
 * different reason: the resume leg is enqueued and returns, and the state flips
 * only when that leg's invocation claims the run, which can take over ten
 * minutes. **`run.resume` is that window**, and only it: the server says
 * `queued` from the accepted press until the claim, and `not_picked_up` once the
 * claim is overdue, so the window holds across a reload and in every tab and
 * ends when the server says so. No mark is made or read for it.
 */
export function isResumingWindow(run: RunDetail, marked: boolean): boolean {
  if (run.state === 'failed') return run.resume?.status === 'queued';
  if (!isAwaiting(run.state)) return false;
  // Explicit `false`, so a payload missing the field falls through to the mark
  // and the run reads as still asking rather than resuming.
  if (run.gated === false) return true;
  return marked;
}

/**
 * The run is **moving**: the pipeline owns it, or its continuation is enqueued
 * and about to. One predicate for the two surfaces that must agree about that —
 * the poll below, and the research page's "Run in progress" lede — so a page
 * cannot claim a run is underway while the client has stopped watching it.
 */
export function isRunMoving(run: RunDetail, marked: boolean): boolean {
  return isWorkingState(run.state) || isResumingWindow(run, marked);
}

/**
 * 3s while the pipeline is working (`created`/`running`/`checkpointed`) and
 * through the resuming window; **no polling in an unanswered `awaiting_*`** —
 * those states move only when a human acts, they are exempt from heartbeat
 * reaping, and a run may sit there for days without the client timing it out or
 * nagging.
 *
 * `checkpointed` is polled for the same reason `running` is: the successor leg
 * claims the run seconds later, and the poll is what shows that happening. It is
 * also what keeps the strip's silence marker live — the marker only appears in
 * `running`, and `running` is polled.
 */
export function shouldPollRun(run: RunDetail, marked: boolean): boolean {
  return isRunMoving(run, marked);
}

/**
 * **Nobody has looked yet** — the state an import leaves a person in.
 *
 * A row committed from a file arrives with facts on it and no run behind it, and
 * the Research tab was drawing a dossier over that: the brief's own block heads
 * with nothing under them, over the imported facts, over a foot that dates the
 * record. Every one of those is a statement about research, and none of them had
 * any research to be about — it read as a page half-written rather than a page
 * not started.
 *
 * The two conditions are both necessary and they are not the same question. No
 * **analyses** is what makes the brief empty. No **run** is what makes it empty
 * *for a reason the office can act on*: a run that finished and produced nothing
 * is a finding in itself, and a run in flight is about to fill the page in —
 * neither should be told "no research yet".
 */
export function noResearchYet(input: {
  analyses: readonly unknown[];
  runs: readonly RunSummary[];
}): boolean {
  return input.analyses.length === 0 && input.runs.length === 0;
}

/* ————————————————————————— an organisation's lifecycle ————————————————————————— */

/**
 * **What an organisation's record offers next**, as four predicates over its run
 * list — the Overview tab's slot at the top and the shell's foot both read them,
 * which is why they live here beside `completedResearchRun` rather than on
 * either surface. A page that decided this for itself and a foot that decided it
 * again would be two opinions about one record.
 */

/**
 * **Which run the strip narrates**, or null where there is none.
 *
 * Until a lookup has ever been started this is null for every organisation,
 * `useRun(null)` is disabled, and the tab shows **nothing** in the strip's place
 * — no head, no empty line. A strip saying "never looked up" on every record
 * would be furniture.
 */
export function latestRunId(runs: readonly RunSummary[]): string | null {
  return runs[0]?.id ?? null;
}

/**
 * **Is the pipeline holding this record?** — the predicate that decides which of
 * the two things stands in the slot at the top, and the one that takes Refresh
 * out of the foot.
 *
 * Non-terminal, which is the server's own `RUN_ALREADY_ACTIVE` rule
 * (`state NOT IN ('complete','failed')`) so the control and the refusal behind
 * it answer the same question from the same rows. A **failed** run is therefore
 * not active: the office should be able to ask again.
 *
 * Since the owner's ruling of 2026-09-09 ("The status bar once a lookup is
 * complete stays put at 'Lookup done' — it should just go away") this is the
 * strip's own condition too, and not only the buttons': the two are exact
 * complements, so the reader presses in one place and is told what is happening
 * in the same place, and when the run ends the telling stops rather than sitting
 * there being finished.
 *
 * The polled run wins where there is one, for the reason the person page's
 * delete control reads `runQuery.data?.state ?? latestRun?.state`: the record is
 * read on a 60s poll and the run on a 3s one, so the record's copy of the state
 * is the stale one for most of a run's life. The foot passes `null` for it and
 * leans on the started id below instead — it polls no run of its own, and the
 * create it owns invalidates the record the moment it settles.
 */
export function organisationRunActive(
  runs: readonly RunSummary[],
  polled: RunState | null,
  startedRunId: string | null = null,
): boolean {
  /* **A run this press just started, which the record has not been read since.**
     Without this the second lookup on a record re-offers the button for the
     length of a round trip: the create returns, the record still lists the
     *previous* completed run, and `polled` is null until the new run's first
     read lands — so both sources say "finished" about a run that is going. */
  if (startedRunId !== null && startedRunId !== latestRunId(runs)) return true;
  const state = polled ?? runs[0]?.state ?? null;
  return state !== null && !isTerminal(state);
}

/**
 * **Does the run block hold the slot?** While the pipeline holds the record
 * (`organisationRunActive`), and also while the latest run is a failed one the
 * server would resume (`RunResume`), with Resume on offer or already waiting for
 * the agent: Resume is then the only action, so neither the lookup buttons nor
 * the foot's Refresh are offered beside it. A failed run Resume refuses (a
 * cancel, an unreadable match) leaves the slot to the buttons.
 *
 * The polled run is read for `resume` only when it is the record's latest, as
 * `organisationRunFailure` reads it.
 */
export function organisationRunHolds(
  runs: readonly RunSummary[],
  polled: Pick<RunDetail, 'id' | 'state' | 'resume'> | null,
  startedRunId: string | null = null,
): boolean {
  if (organisationRunActive(runs, polled?.state ?? null, startedRunId)) return true;
  const latest = runs[0] ?? null;
  const run = polled && (!latest || polled.id === latest.id) ? polled : latest;
  return run?.resume != null;
}

/**
 * **Has a lookup ever finished here?** — which of the two buttons the tab offers
 * (owner ruling, 2026-09-09: "Investigate should be downstream of lookup.
 * Similar to the brief vs id lock on people").
 *
 * The **server's own `LOOKUP_FIRST` rule**, said in the same words: a completed
 * `org_lookup` run on this subject, at either depth. Reading it any other way
 * would put a button on the page that the door behind it refuses.
 *
 * It is about *ever*, not about the latest: what a lookup settles is what the
 * record is, and that does not come undone because a later run failed. A run
 * that failed never counted, because the record learned nothing from it.
 */
export function organisationEverLookedUp(runs: readonly RunSummary[]): boolean {
  return runs.some((run) => run.kind === 'org_lookup' && run.state === 'complete');
}

/**
 * **Has an investigation ever finished here?** — which is what takes the last
 * button off the top of the page and puts Refresh in its foot (owner ruling,
 * 2026-09-10: "once an investigation succeeds, we should remove the button from
 * the top and move it down to the bottom … as a 'Refresh' option, just like with
 * people").
 *
 * `organisationEverLookedUp`'s rule with the depth named, and read off the run's
 * own `lookup_depth` — the depth the office pressed, frozen on the row at
 * creation — rather than re-derived from anything the page can see. *Ever*, for
 * the same reason: a record that has been investigated has been investigated,
 * and a later run that failed does not put the page back a step. That is exactly
 * what the owner was looking at when they ruled, from the other end.
 */
export function organisationEverInvestigated(runs: readonly RunSummary[]): boolean {
  return runs.some(
    (run) =>
      run.kind === 'org_lookup' && run.state === 'complete' && run.lookup_depth === 'investigation',
  );
}
